import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { useCart } from '../context/CartContext';
import { getProductImageUrl, hasProductImage } from '../utils/imageUtils';

interface ProductCardProps {
    product: Product;
    onAddToCart?: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
    const { addToCart, getCartItem, updateQuantity, removeFromCart } = useCart();
    const [imageError, setImageError] = useState(false);

    const cartItem = getCartItem(product.id);

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const handleAddToCart = () => {
        addToCart(product, 1);
        if (onAddToCart) {
            onAddToCart(product);
        }
    };

    const handleIncreaseQuantity = () => {
        if (cartItem) {
            updateQuantity(product.id, cartItem.quantity + 1);
        } else {
            addToCart(product, 1);
        }
        if (onAddToCart) {
            onAddToCart(product);
        }
    };

    const handleDecreaseQuantity = () => {
        if (cartItem) {
            if (cartItem.quantity > 1) {
                updateQuantity(product.id, cartItem.quantity - 1);
            } else {
                removeFromCart(product.id);
            }
        }
    };

    // Determine the image URL to use
    const getImageUrl = () => {
        if (imageError) return null;

        // First try the S3 image if we have a product code
        if (hasProductImage(product)) {
            return getProductImageUrl(product.code!);
        }

        // Fallback to existing image_url or photo_url
        return product.image_url || product.photo_url;
    };

    const imageUrl = getImageUrl();

    return (
        <div className="card group hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
            {/* Product Image */}
            <Link to={`/product/${product.id}`} className="block relative overflow-hidden">
                <div className="h-48 bg-gray-100 flex items-center justify-center relative">
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={product.name || product.designation}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="text-gray-400 text-4xl group-hover:scale-110 transition-transform duration-300">📦</div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
            </Link>

            {/* Product Info */}
            <div className="card-body">
                <Link to={`/product/${product.id}`}>
                    <h3 className="font-semibold text-gray-900 mb-3 text-lg hover:text-primary transition-colors leading-tight line-clamp-2">
                        {product.name || product.designation}
                    </h3>
                </Link>

                {product.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                        {product.description}
                    </p>
                )}

                <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-primary">
                        {formatPrice(product.unit_price || product.price || 0)}
                    </span>
                    <div className="flex flex-col items-end space-y-1">
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-md font-medium">
                            {product.unit}
                        </span>
                        {product.pcb && product.pcb > 1 && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md font-medium border border-blue-200">
                                PCB: {product.pcb}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                        {product.kosher_certification && (
                            <span className="badge badge-accent font-semibold">
                                CASHER
                            </span>
                        )}
                        {product.is_featured && (
                            <span className="badge badge-error">
                                VEDETTE
                            </span>
                        )}
                    </div>
                    <div className={`text-sm font-medium ${product.is_available ? 'text-success' : 'text-error'}`}>
                        {product.is_available ? '✓ Disponible' : '✗ Indisponible'}
                    </div>
                </div>

                {/* Add to Cart / Quantity Controls */}
                {!product.is_available ? (
                    <button
                        disabled
                        className="btn bg-gray-300 text-gray-500 cursor-not-allowed w-full font-semibold"
                    >
                        Indisponible
                    </button>
                ) : cartItem ? (
                    // Show quantity controls if product is in cart
                    <div className="flex items-center justify-center space-x-3 bg-eldai-yellow/20 border-2 border-eldai-yellow rounded-lg p-2">
                        <button
                            onClick={handleDecreaseQuantity}
                            className="w-8 h-8 bg-eldai-yellow hover:bg-eldai-blue hover:text-white text-eldai-blue rounded-full flex items-center justify-center font-bold transition-colors"
                        >
                            −
                        </button>
                        <span className="font-bold text-eldai-blue text-lg min-w-[2rem] text-center">
                            {cartItem.quantity}
                        </span>
                        <button
                            onClick={handleIncreaseQuantity}
                            className="w-8 h-8 bg-eldai-yellow hover:bg-eldai-blue hover:text-white text-eldai-blue rounded-full flex items-center justify-center font-bold transition-colors"
                        >
                            +
                        </button>
                    </div>
                ) : (
                    // Show add to cart button if product is not in cart
                    <button
                        onClick={handleAddToCart}
                        className="btn btn-primary w-full font-semibold transition-all duration-300"
                    >
                        Ajouter au panier
                    </button>
                )}
            </div>
        </div>
    );
};

export default ProductCard;
